/**
 * Schedule Settings Page Component
 * Allows business owners to set and manage working hours for their business
 * @component ScheduleSettings
 */

import React, { useState, useEffect } from 'react';
import { Switch, FormControlLabel } from '@mui/material';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { translate } from '../../locales';
import './ScheduleSettings.css';

interface TimeSlot {
  time: string;
  status: 'available' | 'unavailable' | 'break';
}

interface APITimeSlot {
  start_time: string;
  end_time: string;
  status: 'available' | 'unavailable' | 'break';
}

interface APIDaySchedule {
  day_of_week: number;
  slots: APITimeSlot[];
  is_closed: boolean;
}

interface WorkingHours {
  monday: { slots: TimeSlot[]; closed: boolean };
  tuesday: { slots: TimeSlot[]; closed: boolean };
  wednesday: { slots: TimeSlot[]; closed: boolean };
  thursday: { slots: TimeSlot[]; closed: boolean };
  friday: { slots: TimeSlot[]; closed: boolean };
  saturday: { slots: TimeSlot[]; closed: boolean };
  sunday: { slots: TimeSlot[]; closed: boolean };
}

const ScheduleSettings: React.FC = () => {
  const { language } = useLanguage();
  const { addNotification } = useNotification();
  const { user } = useAuth();
  const { colors } = useTheme();
  const t = (key: any) => translate(key, language as 'en' | 'ua');

  const [workingHours, setWorkingHours] = useState<WorkingHours | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  // Generate default time slots for a day
  const generateDefaultSlots = (): TimeSlot[] => {
    const slots: TimeSlot[] = [];
    for (let hour = 8; hour <= 20; hour++) {
      // Add lunch break at 12:00 instead of regular slot
      if (hour === 12) {
        slots.push({
          time: '12:00',
          status: 'break'
        });
      } else {
        slots.push({
          time: `${hour.toString().padStart(2, '0')}:00`,
          status: 'available'
        });
      }
    }
    return slots;
  };

  // Initialize with default schedule
  useEffect(() => {
    const defaultSchedule: WorkingHours = {
      monday: { closed: false, slots: generateDefaultSlots() },
      tuesday: { closed: false, slots: generateDefaultSlots() },
      wednesday: { closed: false, slots: generateDefaultSlots() },
      thursday: { closed: false, slots: generateDefaultSlots() },
      friday: { closed: false, slots: generateDefaultSlots() },
      saturday: { closed: true, slots: [] },
      sunday: { closed: true, slots: [] },
    };
    setWorkingHours(defaultSchedule);
    loadExistingSchedule();
  }, []);

  const loadExistingSchedule = async () => {
    if (!user?.id) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No auth token');
      }

      // Get user's business settings to find business ID
      const businessResponse = await fetch('/api/v1/business', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      let businessId = user.id; // fallback to user ID

      if (businessResponse.ok) {
        const businessData = await businessResponse.json();
        console.log('Business data from API:', businessData);
        if (Array.isArray(businessData) && businessData.length > 0) {
          businessId = businessData[0].id; // Use first business ID
        }
      } else {
        console.log('Failed to get business data, status:', businessResponse.status);
        if (businessResponse.status === 401) {
          throw new Error(language === 'ua' ? 'Помилка авторизації' : 'Authentication error');
        }
        // Continue with user ID as fallback for other errors
      }

      console.log('Loading schedule for business ID:', businessId);

      const response = await fetch(`/api/v1/working-hours/${businessId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Loaded schedule data:', data);

        // Convert API response format to component format
        const convertedWorkingHours: WorkingHours = {
          monday: {
            closed: data.monday?.length === 0,
            slots: (data.monday || []).map((slot: any) => ({
              time: slot.start_time,
              status: slot.status
            }))
          },
          tuesday: {
            closed: data.tuesday?.length === 0,
            slots: (data.tuesday || []).map((slot: any) => ({
              time: slot.start_time,
              status: slot.status
            }))
          },
          wednesday: {
            closed: data.wednesday?.length === 0,
            slots: (data.wednesday || []).map((slot: any) => ({
              time: slot.start_time,
              status: slot.status
            }))
          },
          thursday: {
            closed: data.thursday?.length === 0,
            slots: (data.thursday || []).map((slot: any) => ({
              time: slot.start_time,
              status: slot.status
            }))
          },
          friday: {
            closed: data.friday?.length === 0,
            slots: (data.friday || []).map((slot: any) => ({
              time: slot.start_time,
              status: slot.status
            }))
          },
          saturday: {
            closed: data.saturday?.length === 0,
            slots: (data.saturday || []).map((slot: any) => ({
              time: slot.start_time,
              status: slot.status
            }))
          },
          sunday: {
            closed: data.sunday?.length === 0,
            slots: (data.sunday || []).map((slot: any) => ({
              time: slot.start_time,
              status: slot.status
            }))
          },
        };

        setWorkingHours(convertedWorkingHours);
      } else {
        console.log('Failed to load schedule, status:', response.status);
        
        // Handle specific error cases
        if (response.status === 401) {
          setError(language === 'ua' ? 'Помилка авторизації. Будь ласка, увійдіть знову.' : 'Authentication error. Please log in again.');
        } else if (response.status === 404) {
          setError(language === 'ua' ? 'Бізнес не знайдено. Спочатку налаштуйте/активуйте свій бізнес.' : 'Business not found. Please set up your business first.');
        } else if (response.status >= 500) {
          setError(language === 'ua' ? 'Помилка сервера. Спробуйте пізніше.' : 'Server error. Please try again later.');
        } else {
          setError(language === 'ua' ? 'Не вдалося завантажити графік роботи' : 'Failed to load working schedule');
        }
      }
      // If response is not ok, we'll use the default schedule that's already set
    } catch (err) {
      console.error('Error loading schedule:', err);
      setError(language === 'ua' ? 'Не вдалося завантажити графік' : 'Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleSlotStatusChange = (day: keyof WorkingHours, slotIndex: number, newStatus: 'available' | 'unavailable' | 'break') => {
    if (!workingHours) return;

    const updatedHours = { ...workingHours };
    updatedHours[day] = {
      ...updatedHours[day],
      slots: updatedHours[day].slots.map((slot, index) =>
        index === slotIndex ? { ...slot, status: newStatus } : slot
      )
    };
    setWorkingHours(updatedHours);
  };

  const handleDayToggle = (day: keyof WorkingHours) => {
    if (!workingHours) return;

    const updatedHours = { ...workingHours };
    const isClosed = !updatedHours[day].closed;
    updatedHours[day] = {
      closed: isClosed,
      slots: isClosed ? [] : generateDefaultSlots()
    };
    setWorkingHours(updatedHours);
  };

  const saveSchedule = async () => {
    if (!workingHours || !user?.id) return;

    setSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No auth token');
      }

      // Get user's business settings to find business ID
      const businessResponse = await fetch('/api/v1/business', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      let businessId = user.id; // fallback to user ID

      if (businessResponse.ok) {
        const businessData = await businessResponse.json();
        console.log('Business data from API (save):', businessData);
        if (Array.isArray(businessData) && businessData.length > 0) {
          businessId = businessData[0].id; // Use first business ID
        }
      }

      // Convert to API format
      const dayOfWeekMapping = [1, 2, 3, 4, 5, 6, 0]; // Monday = 1, Tuesday = 2, Wednesday = 3, Thursday = 4, Friday = 5, Saturday = 6, Sunday = 0
      const scheduleData: APIDaySchedule[] = days.map((day, index) => ({
        day_of_week: dayOfWeekMapping[index],
        slots: workingHours[day as keyof WorkingHours].closed ? [] : workingHours[day as keyof WorkingHours].slots.map(slot => ({
          start_time: slot.time,
          end_time: slot.time, // For now, end time equals start time since we work with hourly slots
          status: slot.status
        })),
        is_closed: workingHours[day as keyof WorkingHours].closed
      }));

      console.log('Saving schedule for business ID:', businessId);
      console.log('Schedule data to save:', scheduleData);

      const response = await fetch(`/api/v1/working-hours/${businessId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(scheduleData),
      });

      console.log('Save response status:', response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      addNotification(
        language === 'ua' ? '✅ Графік роботи збережено' : '✅ Schedule saved successfully',
        'success'
      );

      // Reload the schedule to show saved data after a short delay
      setTimeout(() => {
        loadExistingSchedule();
      }, 500);
    } catch (err) {
      console.error('Error saving schedule:', err);
      setError(language === 'ua' ? 'Не вдалося зберегти графік' : 'Failed to save schedule');
      addNotification(
        language === 'ua' ? '❌ Помилка збереження графіку' : '❌ Failed to save schedule',
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const formatDayName = (day: string) => {
    const days = {
      monday: language === 'ua' ? 'Понеділок' : 'Monday',
      tuesday: language === 'ua' ? 'Вівторок' : 'Tuesday',
      wednesday: language === 'ua' ? 'Середа' : 'Wednesday',
      thursday: language === 'ua' ? 'Четвер' : 'Thursday',
      friday: language === 'ua' ? 'П\'ятниця' : 'Friday',
      saturday: language === 'ua' ? 'Субота' : 'Saturday',
      sunday: language === 'ua' ? 'Неділя' : 'Sunday',
    };
    return days[day as keyof typeof days] || day;
  };

  if (loading) {
    return (
      <div className="schedule-settings">
        <div className="loading-state">
          <p>{language === 'ua' ? 'Завантаження...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="schedule-settings">
      <div className="settings-header">
        <h1 className="page-title">
          <i className="bi bi-calendar-week"></i>
          {t('pages.scheduleSettings.title') || (language === 'ua' ? 'Налаштування графіку роботи' : 'Schedule Settings')}
        </h1>
        <p className="page-description">
          {language === 'ua' ? 'Налаштуйте години роботи вашого бізнесу' : 'Set your business working hours'}
        </p>
      </div>

      {error && (
        <div className="error-message">
          <i className="bi bi-exclamation-triangle"></i>
          {error}
        </div>
      )}

      <div className="schedule-editor">
        <div className="schedule-grid">
          {workingHours && days.map((day) => (
            <div key={day} className="day-editor">
            <div className="day-header">
              <h3>{formatDayName(day)}</h3>
              <FormControlLabel
                control={
                  <Switch
                    checked={!workingHours[day as keyof WorkingHours].closed}
                    onChange={() => handleDayToggle(day as keyof WorkingHours)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: colors.success,
                        '&:hover': {
                          backgroundColor: `${colors.success}14`,
                        },
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: colors.success,
                      },
                    }}
                  />
                }
                label={language === 'ua' ? 'Відчинено' : 'Open'}
              />
            </div>

            {!workingHours[day as keyof WorkingHours].closed && (
              <div className="time-slots">
                {workingHours[day as keyof WorkingHours].slots.map((slot, slotIndex) => (
                  <div key={slotIndex} className="time-slot">
                    <span className="slot-time">{slot.time}</span>
                    <div className="slot-status-controls">
                      <button
                        className={`status-btn ${slot.status === 'available' ? 'active available' : ''}`}
                        onClick={() => handleSlotStatusChange(day as keyof WorkingHours, slotIndex, 'available')}
                        title={language === 'ua' ? 'Вільно' : 'Available'}
                      >
                        <i className="bi bi-check-circle"></i>
                      </button>
                      <button
                        className={`status-btn ${slot.status === 'break' ? 'active break' : ''}`}
                        onClick={() => handleSlotStatusChange(day as keyof WorkingHours, slotIndex, 'break')}
                        title={language === 'ua' ? 'Закрито' : 'Break'}
                      >
                        <i className="bi bi-pause-circle"></i>
                      </button>
                      <button
                        className={`status-btn ${slot.status === 'unavailable' ? 'active unavailable' : ''}`}
                        onClick={() => handleSlotStatusChange(day as keyof WorkingHours, slotIndex, 'unavailable')}
                        title={language === 'ua' ? 'Зайнято' : 'Busy'}
                      >
                        <i className="bi bi-x-circle"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        </div>
      </div>

      <div className="schedule-actions">
        <button
          className="btn-primary"
          onClick={saveSchedule}
          disabled={saving}
        >
          {saving ? (
            <>
              <i className="bi bi-hourglass-split"></i>
              {language === 'ua' ? 'Збереження...' : 'Saving...'}
            </>
          ) : (
            <>
              <i className="bi bi-check-lg"></i>
              {language === 'ua' ? 'Зберегти графік' : 'Save Schedule'}
            </>
          )}
        </button>
      </div>

      <div className="schedule-legend">
        <div className="legend-item">
          <i className="bi bi-check-circle legend-icon available"></i>
          <span>{language === 'ua' ? 'Вільно' : 'Available'}</span>
        </div>
        <div className="legend-item">
          <i className="bi bi-pause-circle legend-icon break"></i>
          <span>{language === 'ua' ? 'Закрито' : 'Break'}</span>
        </div>
        <div className="legend-item">
          <i className="bi bi-x-circle legend-icon unavailable"></i>
          <span>{language === 'ua' ? 'Зайнято' : 'Busy'}</span>
        </div>
      </div>
    </div>
  );
};

export default ScheduleSettings;
